import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("sv_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("sv_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("sv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("sv_token", data.token);
    setUser(data.user);
    // Stash the OTP payload so OtpVerifyPage can show it without a re-fetch
    sessionStorage.setItem("sv_otp_payload", JSON.stringify({
      otp_code: data.otp_code,
      otp_message: data.otp_message,
      admin_whatsapp: data.admin_whatsapp,
    }));
    return data;
  };

  const verifyOtp = async (code) => {
    const { data } = await api.post("/auth/verify-otp", { code });
    setUser(data);
    sessionStorage.removeItem("sv_otp_payload");
    return data;
  };

  const resendOtp = async () => {
    const { data } = await api.post("/auth/resend-otp");
    setUser(data.user);
    sessionStorage.setItem("sv_otp_payload", JSON.stringify({
      otp_code: data.otp_code,
      otp_message: data.otp_message,
      admin_whatsapp: data.admin_whatsapp,
    }));
    return data;
  };

  const logout = () => {
    localStorage.removeItem("sv_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, verifyOtp, resendOtp, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
